/**
 * Shared Copilot types between frontend and backend
 * Used for Lambda code editor AI assistant
 * Zod schemas for validation + inferred TypeScript types
 */

import { z } from 'zod';

// ============================================
// Zod Schemas
// ============================================

// Patch operation type schema
export const patchOperationTypeSchema = z.enum([
  'create_file',
  'update_file',
  'delete_file',
  'create',
  'update',
  'delete',
]);

// Patch operation schema
export const patchOperationSchema = z.object({
  type: patchOperationTypeSchema,
  path: z.string().optional(),
  diff: z.string(),
});

// Apply patch call schema
export const applyPatchCallSchema = z.object({
  type: z.string(),
  operation: patchOperationSchema,
});

// Apply patch call with ID schema
export const applyPatchCallWithIdSchema = z.object({
  type: z.literal('apply_patch_call'),
  call_id: z.string(),
  operation: patchOperationSchema,
});

// Content part schema (recursive for nested content)
export const copilotContentPartSchema: z.ZodType<CopilotContentPart> = z.lazy(
  () =>
    z.object({
      type: z.enum([
        'text',
        'output_text',
        'apply_patch_call',
        'read_file_call',
        'save_code_call',
        'run_test_call',
        'run_clickhouse_query_call',
        'get_messages_call',
        'web_search_call',
        'message',
      ]),
      text: z.string().optional(),
      operation: patchOperationSchema.optional(),
      path: z.string().optional(),
      payload: z.unknown().optional(),
      offset: z.number().optional(),
      limit: z.number().optional(),
      sql: z.string().optional(),
      toolStatus: z.enum(['completed', 'failed']).optional(),
      toolResult: z.unknown().optional(),
      toolLogs: z.string().optional(),
      toolStatusCode: z.number().optional(),
      toolFunctionError: z.string().optional(),
      toolError: z.string().optional(),
      toolColumns: z
        .array(z.object({ name: z.string(), type: z.string() }))
        .optional(),
      toolRows: z.array(z.unknown()).optional(),
      toolStats: z
        .object({
          rowsRead: z.number(),
          bytesRead: z.number(),
          elapsedMs: z.number(),
          resultRows: z.number(),
          resultBytes: z.number(),
        })
        .optional(),
      exportFilename: z.string().optional(),
      exportS3Key: z.string().optional(),
      exportSizeBytes: z.number().optional(),
      searchQueries: z.array(z.string()).optional(),
      content: z.array(copilotContentPartSchema).optional(),
    }),
);

// Copilot message schema
export const copilotMessageSchema = z.object({
  role: z.enum(['user', 'assistant', 'system']),
  content: z.union([z.string(), z.array(z.any()), z.null()]),
  apply_patch_call: applyPatchCallSchema.optional(),
});

// Supported copilot models (only models that support apply_patch tool)
export const COPILOT_MODELS = [
  { label: 'GPT-5.1', value: 'gpt-5.1' },
  { label: 'GPT-5.1 Codex Max', value: 'gpt-5.1-codex-max' },
  { label: 'GPT-5.2', value: 'gpt-5.2' },
  { label: 'GPT-5.4', value: 'gpt-5.4' },
  { label: 'GPT-5.5', value: 'gpt-5.5' },
] as const;

export const copilotModelSchema = z.enum([
  'gpt-5.1',
  'gpt-5.1-codex-max',
  'gpt-5.2',
  'gpt-5.4',
  'gpt-5.5',
]);

export const DEFAULT_COPILOT_MODEL = 'gpt-5.5' as const;

// Reasoning effort levels for reasoning models
// gpt-5.1 supports: none, low, medium, high
// gpt-5.1-codex-max supports: none, low, medium, high, xhigh
export const REASONING_EFFORT_LEVELS = [
  { label: 'None', value: 'none', description: 'No extended reasoning' },
  {
    label: 'Low',
    value: 'low',
    description: 'Faster responses, less reasoning',
  },
  {
    label: 'Medium',
    value: 'medium',
    description: 'Balanced speed and reasoning',
  },
  {
    label: 'High',
    value: 'high',
    description: 'More thorough reasoning, slower',
  },
  {
    label: 'Extra High',
    value: 'xhigh',
    description:
      'Maximum reasoning depth (gpt-5.1-codex-max, gpt-5.2, gpt-5.4, gpt-5.5)',
  },
] as const;

export const reasoningEffortSchema = z
  .enum(['none', 'low', 'medium', 'high', 'xhigh'])
  .nullable();

/**
 * Constrains effort on reasoning for
 * [reasoning models](https://platform.openai.com/docs/guides/reasoning). Currently
 * supported values are `none`, `low`, `medium`, `high`, and `xhigh`.
 * Reducing reasoning effort can result in faster responses and fewer tokens used
 * on reasoning in a response.
 *
 * - `gpt-5.1` defaults to `none`, which does not perform reasoning. The supported
 *   reasoning values for `gpt-5.1` are `none`, `low`, `medium`, and `high`. Tool
 *   calls are supported for all reasoning values in gpt-5.1.
 * - `xhigh` is supported for `gpt-5.1-codex-max`, `gpt-5.2`, `gpt-5.4`, and `gpt-5.5`.
 */
export type ReasoningEffort =
  | 'none'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | null;

export const DEFAULT_REASONING_EFFORT: ReasoningEffort = 'medium';

// Copilot chat request schema (for validation)
export const copilotChatSchema = z.object({
  messages: z
    .array(copilotMessageSchema)
    .min(1, 'At least one message required'),
  activeFile: z.string().default(''),
  files: z.record(z.string(), z.string()).default({}),
  model: copilotModelSchema.default('gpt-5.1'),
  reasoningEffort: reasoningEffortSchema.optional(),
});

// Report patch result schema (for validation)
export const reportPatchResultSchema = z.object({
  previous_response_id: z.string().min(1, 'previous_response_id is required'),
  patch_results: z
    .array(
      z.object({
        call_id: z.string().min(1, 'call_id is required'),
        status: z.enum(['completed', 'failed']),
        output: z.string().optional(),
      }),
    )
    .min(1, 'At least one patch result required'),
  model: copilotModelSchema.default('gpt-5.1'),
  reasoningEffort: reasoningEffortSchema.optional(),
});

// Copilot response data schema
export const copilotResponseDataSchema = z.object({
  message: copilotMessageSchema,
  apply_patch_calls: z.array(applyPatchCallWithIdSchema),
  response_id: z.string(),
});

// ============================================
// Streaming (Agent mode) — SSE
// ============================================

// Streaming chat request — same shape as classic copilot input.
// Kept as a separate schema so we can evolve agent mode independently
// (e.g. add tools, max_iterations) without touching the classic endpoint.
export const copilotChatStreamSchema = copilotChatSchema.extend({
  maxIterations: z.number().int().min(1).max(50).optional(),
});

export type CopilotChatStreamInput = z.infer<typeof copilotChatStreamSchema>;

// =====================================================================
// Iterate endpoint — frontend-driven agent loop
// =====================================================================
// Frontend owns the loop and tool execution; backend just proxies one
// OpenAI Responses-API call per iteration. Schema is intentionally
// permissive (z.unknown) on input/tools because we forward them verbatim
// and OpenAI's own SDK validates the shape upstream.

export const copilotIterateSchema = z.object({
  // OpenAI Responses API `input` array — either initial messages or
  // tool-call outputs from the previous iteration.
  input: z.array(z.unknown()).min(1, 'input is required'),
  // Tool definitions the model may call this iteration. Frontend rebuilds
  // these per request so adding/removing a tool needs no backend change.
  tools: z.array(z.unknown()).optional(),
  previous_response_id: z.string().optional(),
  model: copilotModelSchema.default('gpt-5.1'),
  reasoningEffort: reasoningEffortSchema.optional(),
});

export type CopilotIterateInput = z.infer<typeof copilotIterateSchema>;

// =====================================================================
// Copilot ClickHouse query — backend-executed tool. Frontend sends the
// model-issued SQL to /v1/org/code/copilot/clickhouse-query; backend
// runs it as the copilot service user with the org's role active so
// the row policy filters by the caller's business_ids.
// =====================================================================

// OpenAI strict-mode tool calling forces the model to emit every
// declared property, even optionals — so we get `filename: ""` instead
// of an omitted key. Treat empty / whitespace-only strings on optional
// fields as "not provided" rather than rejecting them.
const optionalLlmString = (max: number) =>
  z.preprocess(
    v => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    z.string().min(1).max(max).optional(),
  );

export const copilotClickhouseQuerySchema = z.object({
  sql: z.string().min(1, 'sql is required'),
  // When set to 's3', rows are written directly to copilot scratch S3
  // and the response contains only {rowCount, s3Key, bucket} — used for
  // exports too large to round-trip through chat context.
  saveAs: z.enum(['rows', 's3']).optional(),
  // Filename to use in the S3 key when saveAs='s3'. Defaults to a
  // timestamped name. Only the basename is used; sanitised server-side.
  filename: optionalLlmString(255),
  // Session bucket prefix when saveAs='s3'. Caller usually passes the
  // editor's scratch sessionId so uploads + exports + outputs share
  // the same `{orgId}/{sessionId}/...` tree and lifecycle.
  sessionId: optionalLlmString(64),
});

export type CopilotClickhouseQueryInput = z.infer<
  typeof copilotClickhouseQuerySchema
>;

// =====================================================================
// Copilot contact-debug — backend-executed tool. Frontend sends a
// clientWaNumber + businessId; backend renders a narrative timeline of
// the contact's messages + current state so the model can debug
// chatbot behavior. See `formatChatbotDebugContext` on the backend.
// =====================================================================

export const copilotContactDebugSchema = z.object({
  clientWaNumber: z.string().min(1, 'clientWaNumber is required'),
  // Optional — backend falls back to the caller's JWT business if the
  // frontend doesn't pin one.
  businessId: z.number().int().positive().optional(),
  // IANA timezone (e.g. 'Asia/Kolkata') from the browser so the rendered
  // narrative carries local timestamps. Falls back to UTC if absent.
  timeZone: z.string().min(1).max(64).optional(),
});

export type CopilotContactDebugInput = z.infer<
  typeof copilotContactDebugSchema
>;

// =====================================================================
// Copilot diff-versions — backend-executed tool. Frontend sends two
// version qualifiers; backend fetches both code bundles, expands them
// and returns a per-file unified diff so the model can explain what
// changed (and what was fixed) between two published versions.
// =====================================================================

export const copilotDiffVersionsSchema = z.object({
  // Version qualifiers — a number like '5', or '$LATEST' for the draft.
  from: z.string().min(1, 'from version is required'),
  to: z.string().min(1, 'to version is required'),
});

export type CopilotDiffVersionsInput = z.infer<
  typeof copilotDiffVersionsSchema
>;

/** One changed file between two versions — a unified diff plus its status. */
export interface VersionFileDiff {
  path: string;
  status: 'added' | 'deleted' | 'modified';
  diff: string;
}

// =====================================================================
// Tool descriptions — single source of truth.
// Backend sends these to OpenAI in the tool definitions (so the model
// knows when to call them). Frontend shows the same text in the UI so
// the user sees exactly what the model was told the tool does.
// =====================================================================

export const APPLY_PATCH_TOOL_DESCRIPTION =
  'Apply a unified-diff patch to create, update, or delete a file in the project.';

// Max characters a single read_file (or its cross-turn replay) feeds the
// model. Large files page via offset/limit instead of being cut blind.
export const READ_FILE_MAX_CHARS = 48_000;

// Editor/zip contract: each chatbot/<name>.json pairs with a
// chatbot/<name>.systemprompt.md holding the prompt as plain markdown.
// Shared because the frontend editor writes the pair and the backend
// deploy-sync parses it — the suffix must never drift between them.
export const SYSTEM_PROMPT_SUFFIX = '.systemprompt.md';

const CHATBOT_DIR = '/chatbot/';

// Bot file names derive from user-supplied bot names: strip anything outside
// a safe charset (kills '/' nesting). The bot id stays in the file contents
// (identity); the filename is the human-readable label, so bot names are
// expected to be unique within an org.
const sanitizeBotFileName = (name: string): string => {
  const cleaned = name
    .replace(/[^a-zA-Z0-9._ -]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[\s-]+|[\s-]+$/g, '')
    .slice(0, 60)
    .replace(/^[\s-]+|[\s-]+$/g, '');
  return cleaned || 'flow';
};

export const isChatbotJsonPath = (path: string): boolean =>
  path.startsWith(CHATBOT_DIR) && path.endsWith('.json');

export const chatbotJsonPath = (name: string): string =>
  `${CHATBOT_DIR}${sanitizeBotFileName(name)}.json`;

export const isChatbotPromptPath = (path: string): boolean =>
  path.startsWith(CHATBOT_DIR) && path.endsWith(SYSTEM_PROMPT_SUFFIX);

export const promptPathForChatbotJson = (jsonPath: string): string =>
  jsonPath.slice(0, -'.json'.length) + SYSTEM_PROMPT_SUFFIX;

export const chatbotJsonPathForPrompt = (mdPath: string): string =>
  mdPath.slice(0, -SYSTEM_PROMPT_SUFFIX.length) + '.json';

// Flow bots live under /flowbot/ as a config JSON + a `.flow.json` companion
// holding only the flowDefinition graph — same split as a chatbot's config JSON
// + `.systemprompt.md`, so config keys stay small/copilot-editable and the big
// graph lives beside it. Older single-file flow bots (flowDefinition inline in
// the config JSON) still load via the "inline wins / companion fills the gap"
// rule and migrate to the pair on the next save.
export const FLOWBOT_DIR = '/flowbot/';

export const FLOW_DEFINITION_SUFFIX = '.flow.json';

// Config file: a /flowbot/*.json that is NOT the .flow.json companion.
export const isFlowBotJsonPath = (path: string): boolean =>
  path.startsWith(FLOWBOT_DIR) &&
  path.endsWith('.json') &&
  !path.endsWith(FLOW_DEFINITION_SUFFIX);

export const isFlowBotDefinitionPath = (path: string): boolean =>
  path.startsWith(FLOWBOT_DIR) && path.endsWith(FLOW_DEFINITION_SUFFIX);

export const flowBotJsonPath = (name: string): string =>
  `${FLOWBOT_DIR}${sanitizeBotFileName(name)}.json`;

export const flowDefinitionPathForFlowBotJson = (jsonPath: string): string =>
  jsonPath.slice(0, -'.json'.length) + FLOW_DEFINITION_SUFFIX;

export const flowBotJsonPathForDefinition = (flowPath: string): string =>
  flowPath.slice(0, -FLOW_DEFINITION_SUFFIX.length) + '.json';

// Python flow bots live under /python_flowbot/ (and ONLY there): each bot is a
// `<name>.py` (the step machine) paired with a `<name>.json` (settings), matched by
// base name and nestable at any depth (e.g. /python_flowbot/sales/lead.{py,json}).
// The org Lambda's built-in handler loads & runs the script (dispatched by
// chatbot_category); the backend stores only { step, vars }. A `.py` with no matching
// `.json` is an importable helper module, not a bot.
export const isPyFlowScriptPath = (path: string): boolean =>
  /^\/python_flowbot\/(?:[^/]+\/)*[^/]+\.py$/.test(path);

export const isPyFlowConfigPath = (path: string): boolean =>
  /^\/python_flowbot\/(?:[^/]+\/)*[^/]+\.json$/.test(path);

// A pyflow file's bot name = its path under /python_flowbot/ without the extension
// (may be nested, e.g. "sales/lead"). '' for any non-pyflow path.
export const pyFlowBotName = (path: string): string =>
  isPyFlowScriptPath(path) || isPyFlowConfigPath(path)
    ? path.slice('/python_flowbot/'.length).replace(/\.(py|json)$/, '')
    : '';

// System prompt files are read whole by default — they ship to the bot's
// own LLM on every message, so any working prompt is context-sized by
// nature. The ceiling only guards against a file that is clearly NOT a
// prompt (e.g. a data dump pasted by mistake): past it, reads fall back
// to paging so one absurd file can't blow the copilot's context.
export const PROMPT_FULL_READ_MAX_CHARS = 300_000;

/**
 * Read policy for the copilot's file tools: prompt files come back whole
 * (unless absurdly large, or the model explicitly asked for a window);
 * everything else goes through pageFileForModel's budget + paging.
 */
export const readFileForModel = (
  path: string,
  content: string,
  offset?: number,
  limit?: number,
): string => {
  if (
    isChatbotPromptPath(path) &&
    offset === undefined &&
    limit === undefined &&
    content.length <= PROMPT_FULL_READ_MAX_CHARS
  ) {
    return content;
  }
  return pageFileForModel(content, offset, limit);
};

/**
 * Window a file for the copilot's read_file tool (and its cross-turn
 * replay, and the Active File section — one budget, one trailer).
 *
 * Normal files page by 1-based line offset/limit. A single-line file
 * larger than the budget falls back to CHARACTER-based offset/limit —
 * the trailer tells the model which mode is in effect.
 */
export const pageFileForModel = (
  content: string,
  offset?: number,
  limit?: number,
): string => {
  if (!offset && !limit && content.length <= READ_FILE_MAX_CHARS) {
    return content;
  }

  const lines = content.split('\n');
  const total = lines.length;

  // Single-line monster: line paging can't advance, so page by chars.
  if (total === 1 && content.length > READ_FILE_MAX_CHARS) {
    const start = Math.min(Math.max(offset ?? 1, 1), content.length);
    const count = Math.min(limit || READ_FILE_MAX_CHARS, READ_FILE_MAX_CHARS);
    const text = content.slice(start - 1, start - 1 + count);
    const end = start - 1 + text.length;
    return (
      `${text}\n\n[read_file: single-line file — showing chars ${start}-${end} of ${content.length}. ` +
      `For THIS file offset/limit are CHARACTER-based; call read_file with offset=${end + 1} to continue.]`
    );
  }

  const start = Math.min(Math.max(offset ?? 1, 1), total);
  const window = lines.slice(start - 1, limit ? start - 1 + limit : total);
  let kept = window.length;
  let text = window.join('\n');
  let cutMidLine = false;
  let charTruncated = false;
  if (text.length > READ_FILE_MAX_CHARS) {
    charTruncated = true;
    const cut = text.slice(0, READ_FILE_MAX_CHARS);
    const lastNl = cut.lastIndexOf('\n');
    // Stay line-aligned so the model can page with offset; if the very
    // first line of the window exceeds the budget, cut mid-line and say so.
    if (lastNl >= 0) {
      text = cut.slice(0, lastNl);
      kept = text === '' ? 1 : text.split('\n').length;
    } else {
      text = cut;
      kept = 1;
      cutMidLine = true;
    }
  }
  if (!charTruncated && start === 1 && kept === total) return text;
  const end = start - 1 + kept;
  const midLineNote = cutMidLine
    ? ` Line ${start} alone exceeds the ${READ_FILE_MAX_CHARS}-char budget and was cut mid-line; its remainder cannot be paged.`
    : '';
  return (
    `${text}\n\n[read_file: showing lines ${start}-${end} of ${total} ` +
    `(file is ${content.length} chars).${midLineNote} Call read_file with offset/limit to read more.]`
  );
};

export const READ_FILE_TOOL_DESCRIPTION =
  'Read the contents of a file from the project by its path. Returns the latest content (after any patches you have applied this turn). ' +
  'Very large files are paged: the response tail tells you which line range you got out of how many total — call read_file again with offset (1-based start line) and limit (number of lines) to read the rest.';

export const SAVE_CODE_TOOL_DESCRIPTION =
  'Persist the current files to the deployment ($LATEST). ONLY call this after you have edited one or more files in this turn (via apply_patch). Do NOT call save_code if you have not made any edits — there is no auto-save, but there is also no point saving an unchanged tree. Calling save_code when nothing changed is wasted compute and confuses the user.';

export const RUN_CLICKHOUSE_QUERY_TOOL_DESCRIPTION =
  "Run a read-only ClickHouse SQL query against the analytics database to inspect production data (messages, conversations, etc.). The query is executed as the current org — a row policy automatically restricts results to this org's business_ids, so do NOT add a `business_id IN (...)` filter; ClickHouse applies it. Use this when the user asks anything about real data: counts, recent messages, distributions, top-N, error rates, etc.\n\n" +
  'WORKFLOW:\n' +
  "1. If you don't know the schema, first query system.tables / system.columns to discover available tables and columns. Example: `SELECT name FROM system.tables WHERE database = currentDatabase() ORDER BY name` then `SELECT name, type FROM system.columns WHERE database = currentDatabase() AND table = '<table>'`.\n" +
  '2. Write a focused SELECT. Prefer `LIMIT N` for previews and aggregate functions for distributions.\n' +
  '3. Inspect the result + scan stats (rows scanned, bytes read, elapsed). If a query scans far more than expected, narrow it with date / status / type filters before drilling deeper.\n\n' +
  'RULES:\n' +
  '- Read-only: only SELECT-style queries succeed. Anything else (INSERT, ALTER, CREATE, DROP, etc.) will be rejected by ClickHouse — do not attempt them.\n' +
  '- Do NOT add `business_id` filters yourself; row policy handles it.\n' +
  '- Use ClickHouse SQL dialect (e.g. `now64()`, `toDateTime()`, `toStartOfHour()`, `arrayJoin()`).\n' +
  '- Quote string literals with single quotes, identifiers with backticks if needed.\n' +
  '- Returns the rows + a stats card (rows scanned, bytes scanned, elapsed ms). The user sees both.';

export const GET_MESSAGES_TOOL_DESCRIPTION =
  "Fetch a contact's full chatbot debug narrative by WhatsApp number — current state (bot on/off, assigned chatbot, session expiry, attributes, assigned agents) plus the complete message timeline (every message ever exchanged, oldest → newest) annotated with status (delivered/read/failed + failure reasons), template names, the chatbot that produced each bot reply, button/list-reply payloads, and session-clear events. Use this whenever the user is debugging chatbot behavior for a specific contact (e.g. 'why did the bot say X to 919876543210?', 'this number got the wrong reply', 'check the last conversation for <number>'). After calling, correlate the narrative against the chatbot code in this editor to pinpoint where the wrong branch fired. Scoped to the user's current business automatically — you only supply the WhatsApp number.";

export const RUN_TEST_TOOL_DESCRIPTION =
  'Invoke the saved code by passing the COMPLETE Lambda event as ONE JSON-encoded string in the `event` parameter. The event MUST include `function_name` and all the fields the function expects.\n\n' +
  'STRICT WORKFLOW (sequential, never parallel):\n' +
  '1. Use read_file on `function_definitions/<function_name>.json` and WAIT for the content.\n' +
  '2. Parse that schema. Identify every required field.\n' +
  '3. For each required field generate a realistic value (use "e.g." hints in descriptions; fall back to: city→"Mumbai", phone→"919999999999", email→"test@example.com", date→"2026-01-01", id→"test_id_001").\n' +
  '4. Build a flat JSON object: `{ "function_name": "<name>", ...all_required_fields_with_values }`. Then JSON.stringify it and pass as `event`.\n' +
  '5. If — and only if — you edited files in this turn, call save_code BEFORE run_test so the deployment reflects your edits. If you only want to test the existing deployed code, skip save_code entirely.\n\n' +
  'EXAMPLES:\n' +
  '- get_weather → event: `{"function_name":"get_weather","city":"Mumbai"}`\n' +
  '- create_lead → event: `{"function_name":"create_lead","name":"Rahul","phone":"919999999999"}`\n' +
  '- chatbot_flow → event: `{"function_name":"chatbot_flow","action":"create_lead","payload":{"name":"Rahul","phone":"919999999999"}}`\n\n' +
  'RULES:\n' +
  '- Never include a `business` field — the frontend injects business context (id, phoneNumberId, countryCode, bizWhatsappNumber, businessAccountId, fbAppId) automatically.\n' +
  '- If the function needs a customer phone number, include `clientWaNumber` at the top of the event (e.g. "919999999999"); the frontend moves it inside the business object.\n' +
  '- Never call run_test in the same turn as read_file — read_file must complete first.\n\n' +
  'Returns the Lambda response and execution logs.';

export const DIFF_VERSIONS_TOOL_DESCRIPTION =
  'Show what changed between two published code versions, as a per-file unified diff. Use this whenever the user asks what changed / was fixed / was added in a version, or to compare two versions (e.g. "what did v7 fix?", "diff v5 and v8", "what changed since the deployed version?").\n\n' +
  'Versions are identified by a qualifier: a number like "7", or "$LATEST" for the current draft. `from` is the older/baseline version, `to` is the newer one. To explain what a SINGLE version changed, set `to` to that version and `from` to the version right before it.\n\n' +
  'Returns each changed file with its status (added / deleted / modified) and a unified diff. Read-only — it never edits or deploys anything.';

/**
 * Build the tool array passed to OpenAI Responses API. Frontend rebuilds
 * this every iteration; backend just forwards to OpenAI. Adding a new
 * tool means a one-line append here plus a frontend executor.
 */
export const buildCopilotTools = () =>
  [
    {
      type: 'function' as const,
      name: 'read_file',
      description: READ_FILE_TOOL_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {
          path: {
            type: 'string',
            description: 'Path of the file to read (e.g. "/handler.py").',
          },
          offset: {
            type: 'number',
            description:
              '1-based line number to start reading from. Omit (or 0) to start at the first line. Use together with limit to page through large files.',
          },
          limit: {
            type: 'number',
            description:
              'Maximum number of lines to return. Omit (or 0) to read as much as fits in one response.',
          },
        },
        required: ['path'],
        additionalProperties: false,
      },
    },
    {
      type: 'function' as const,
      name: 'run_test',
      description: RUN_TEST_TOOL_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {
          event: {
            type: 'string',
            description:
              'JSON-encoded Lambda event. Flat object containing "function_name" plus every required field for that function (per its schema in function_definitions/<function_name>.json). Examples: ' +
              '`{"function_name":"get_weather","city":"Mumbai"}` ; ' +
              '`{"function_name":"create_lead","name":"Rahul","phone":"919999999999"}` ; ' +
              '`{"function_name":"chatbot_flow","action":"create_lead","payload":{"name":"Rahul","phone":"919999999999"}}`. ' +
              'Do NOT include a "business" field. If a customer phone is needed include "clientWaNumber" at the top level.',
          },
        },
        required: ['event'],
        additionalProperties: false,
      },
    },
    {
      type: 'function' as const,
      name: 'run_clickhouse_query',
      description: RUN_CLICKHOUSE_QUERY_TOOL_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {
          sql: {
            type: 'string',
            description:
              'A single ClickHouse SQL SELECT statement. Do NOT add business_id filters — the row policy handles tenant scoping. Examples: ' +
              '`SELECT count() FROM messages WHERE timestamp > now() - INTERVAL 24 HOUR` ; ' +
              '`SELECT status, count() FROM messages WHERE timestamp > now() - INTERVAL 7 DAY GROUP BY status ORDER BY count() DESC` ; ' +
              "`SELECT name, type FROM system.columns WHERE database = currentDatabase() AND table = 'messages'`.",
          },
          saveAs: {
            type: 'string',
            enum: ['rows', 's3'],
            description:
              "Default 'rows' — result is returned inline (rows + columns + stats), use for previews and small aggregates. " +
              "Set to 's3' when the result will be > ~10k rows or > ~5 MB: the CSV is written directly to copilot scratch S3 under {orgId}/{sessionId}/inputs/, " +
              'and the response carries only {s3Key, bucket, rowCount} so chat context stays small. The exported file then appears as a linked S3 file ' +
              'and copilot Python can read it via boto3 in the same lambda. Use this for full-table exports or any query the user calls "all messages", "everything", "full export".',
          },
          filename: {
            type: 'string',
            description:
              'Only used when saveAs="s3" — suggested filename for the export (e.g. "messages_jan2026.csv"); server appends .csv if missing. When saveAs is "rows" or omitted, pass an empty string "".',
          },
        },
        required: ['sql'],
        additionalProperties: false,
      },
    },
    {
      type: 'function' as const,
      name: 'get_messages',
      description: GET_MESSAGES_TOOL_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {
          clientWaNumber: {
            type: 'string',
            description:
              'WhatsApp number with country code, no "+" or spaces (e.g. "919876543210").',
          },
        },
        required: ['clientWaNumber'],
        additionalProperties: false,
      },
    },
    {
      type: 'function' as const,
      name: 'diff_versions',
      description: DIFF_VERSIONS_TOOL_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {
          from: {
            type: 'string',
            description:
              'Baseline (older) version qualifier — a number like "5", or "$LATEST". To explain a single version, set this to the version just before it.',
          },
          to: {
            type: 'string',
            description:
              'Target (newer) version qualifier — a number like "8", or "$LATEST" for the current draft.',
          },
        },
        required: ['from', 'to'],
        additionalProperties: false,
      },
    },
    // Built-in web search — model issues queries directly; results are
    // included inline in the next message and emitted as web_search_call
    // items in the response output so the UI can show what was searched.
    { type: 'web_search' as const },
    // Write tools last: they are the only tools dropped while the editor is
    // read-only (see selectCopilotTools), so keeping them at the tail leaves
    // the read-only tool set a byte-identical prefix of the full set — the
    // OpenAI prompt cache stays warm when the edit lock toggles mid-session.
    { type: 'apply_patch' as const },
    {
      type: 'function' as const,
      name: 'save_code',
      description: SAVE_CODE_TOOL_DESCRIPTION,
      parameters: {
        type: 'object',
        properties: {},
        required: [],
        additionalProperties: false,
      },
    },
  ] as const;

/**
 * Discriminated union describing every tool the agent can invoke.
 * Add a new variant here when adding a new tool — the SSE event types
 * below pick this up automatically and the frontend gets an exhaustive
 * switch on the rendering side.
 */
export type CopilotToolCall =
  | { tool: 'apply_patch'; operation: PatchOperation }
  | { tool: 'read_file'; path: string }
  | { tool: 'save_code' }
  | { tool: 'run_test'; payload: unknown }
  | { tool: 'run_clickhouse_query'; sql: string }
  | { tool: 'diff_versions'; from: string; to: string };

/**
 * Result of executing a tool call client-side, fed back to the model in
 * the next iteration. Frontend dispatches this on tool_call_done so the
 * UI can render the tool's actual outcome (logs, response, error etc.).
 */
export type CopilotToolResult =
  | { tool: 'apply_patch'; status: 'completed' | 'failed'; error?: string }
  | { tool: 'read_file'; status: 'completed' | 'failed'; bytes?: number }
  | { tool: 'save_code'; status: 'completed' | 'failed'; error?: string }
  | {
      tool: 'run_test';
      status: 'completed' | 'failed';
      result?: unknown;
      logs?: string;
      statusCode?: number;
      functionError?: string;
      error?: string;
    }
  | {
      tool: 'run_clickhouse_query';
      status: 'completed' | 'failed';
      columns?: Array<{ name: string; type: string }>;
      rows?: unknown[];
      stats?: ClickhouseQueryStats;
      error?: string;
    };

/**
 * Per-query stats from the ClickHouse server, surfaced in the chat UI so
 * the user (and the model) can see what each query actually scanned.
 */
export interface ClickhouseQueryStats {
  rowsRead: number;
  bytesRead: number;
  elapsedMs: number;
  resultRows: number;
  resultBytes: number;
}

/**
 * SSE event payloads streamed from /v1/org/code/copilot/stream.
 * Frontend incrementally updates UI as these arrive.
 */
export type CopilotStreamEvent =
  | { type: 'iteration_start'; iteration: number }
  | { type: 'model_text'; iteration: number; text: string }
  | ({
      type: 'tool_call_start';
      iteration: number;
      call_id: string;
    } & CopilotToolCall)
  | {
      type: 'tool_call_done';
      iteration: number;
      call_id: string;
      // Short summary for UI (e.g. "+12 -3" for patches, "1.2KB" for reads).
      summary?: string;
    }
  | {
      type: 'iteration_done';
      iteration: number;
      response_id: string;
      hasMoreCalls: boolean;
    }
  | {
      type: 'done';
      final_response_id: string;
      total_iterations: number;
    }
  | { type: 'error'; message: string };

/**
 * Content parts inside a streamed assistant message (Redux state).
 * Mirrors CopilotContentPart but adds a 'read_file_call' variant so the
 * shared CopilotChat renderer can show "📄 Read foo.ts" cards inline.
 */
export interface CopilotReadFileContentPart {
  type: 'read_file_call';
  path: string;
}

// ============================================
// Inferred TypeScript Types
// ============================================

export type PatchOperationType = z.infer<typeof patchOperationTypeSchema>;
export type PatchOperation = z.infer<typeof patchOperationSchema>;
export type ApplyPatchCall = z.infer<typeof applyPatchCallSchema>;
export type ApplyPatchCallWithId = z.infer<typeof applyPatchCallWithIdSchema>;
export type CopilotMessage = z.infer<typeof copilotMessageSchema>;
export type CopilotModel = z.infer<typeof copilotModelSchema>;
export type CopilotChatInput = z.infer<typeof copilotChatSchema>;
export type ReportPatchResultInput = z.infer<typeof reportPatchResultSchema>;
export type CopilotResponseData = z.infer<typeof copilotResponseDataSchema>;

// Content part interface (needed for recursive type)
// Each variant carries just enough data to render its card in the chat
// bubble. Tool-call parts mirror the CopilotToolCall + CopilotToolResult
// unions so a future tool only needs an addition here + a renderer.
export interface CopilotContentPart {
  type:
    | 'text'
    | 'output_text'
    | 'apply_patch_call'
    | 'read_file_call'
    | 'save_code_call'
    | 'run_test_call'
    | 'run_clickhouse_query_call'
    | 'get_messages_call'
    | 'diff_versions_call'
    | 'web_search_call'
    | 'message';
  text?: string;
  operation?: PatchOperation;
  // Set when type='read_file_call' — path the agent fetched.
  // Set when type='get_messages_call' — the WhatsApp number queried
  // (reusing the field keeps the card-render union compact).
  path?: string;
  // Set when type='read_file_call' and the agent paged — the window it
  // requested, so cross-turn replay re-serves the SAME window (not the
  // file head) via pageFileForModel.
  offset?: number;
  limit?: number;
  // Set when type='run_test_call' — request payload the model sent.
  payload?: unknown;
  // Set when type='run_clickhouse_query_call' — the SQL the model issued.
  sql?: string;
  // Final tool execution status — drives card colour (✅ / ❌ / ⚠️).
  toolStatus?: 'completed' | 'failed';
  // run_test result fields (only set on type='run_test_call').
  toolResult?: unknown;
  toolLogs?: string;
  toolStatusCode?: number;
  toolFunctionError?: string;
  toolError?: string;
  // run_clickhouse_query result fields.
  toolColumns?: Array<{ name: string; type: string }>;
  toolRows?: unknown[];
  toolStats?: ClickhouseQueryStats;
  // Set when the query was exported via saveAs='s3'. Carries the
  // info needed to render a Download button directly in the chat card,
  // without the user having to hunt for the file in the editor's
  // linked-files pane.
  exportFilename?: string;
  exportS3Key?: string;
  exportSizeBytes?: number;
  // web_search_call — list of queries the model issued in this single
  // search call (action.queries). Built-in tool, results are merged
  // into the next assistant message automatically; we keep this card
  // visible so the user can see what was searched.
  searchQueries?: string[];
  // diff_versions_call — the two versions compared + the per-file diffs.
  fromVersion?: string;
  toVersion?: string;
  diffFiles?: VersionFileDiff[];
  content?: CopilotContentPart[];
}

// Payload for sending copilot messages (frontend action)
export interface SendCopilotMessagePayload {
  message: string;
  activeFile: string;
  files: Record<string, string>;
  model?: CopilotModel;
  reasoningEffort?: ReasoningEffort;
}

// Payload for reporting patch results (frontend action)
export interface ReportPatchResultPayload {
  previous_response_id: string;
  patch_results: Array<{
    call_id: string;
    status: 'completed' | 'failed';
    output?: string;
  }>;
  model?: CopilotModel;
  reasoningEffort?: ReasoningEffort;
}
